/**
 * Волна 39: боты играют за великанов, суккубов и гоблинов — фракции, которые до
 * волны были запрещены ботам (configs/botExcludedFactions): общий код ботов не
 * знал их механик. Стратегии — developer_artifacts/bot_strategy/faction_strategy
 * (giants.md, succubes.md, goblins.md), правила — utils/bots/factions/*, гейт
 * A/B — isPreW39FactionPlayer (utils/bots/abTestGates).
 *
 * Числа ниже — из стратегий фракций и правил игры, а не подобраны прогонами.
 */

// --- Великаны

// Размер, выше которого растить великана бессмысленно: у большинства фракций
// предел отряда 20 (d20), великану 40 хватает на любой бой (giants.md). До этого
// размера держим рост на казарме и докупаем размер, как обычный отряд до своего
// предела (в общих правилах ботов предел великана — 99)
export const W39_GIANTS_GROWTH_TARGET_SIZE = 40;

// Следующего великана нанимаем, только когда каждый уже нанятый дорос до этого
// размера (giants.md: «нанимать больше 1 великана нужно, когда численность
// первого вырастет хотя бы до 20»). Великаны — до 4, и не больше, чем городов + 1
// (третий и четвертый — под удержание захваченных городов)
export const W39_GIANTS_NEXT_HIRE_MIN_SIZE = 20;

// «Молодой» великан: размер еще сопоставим с обычными отрядами противника, его
// держим вне досягаемости врага (giants.md, первые раунды)
export const W39_GIANTS_YOUNG_SIZE = 12;
// Досягаемость врага для молодого великана: скорость d20 (2) + смежность
export const W39_GIANTS_YOUNG_THREAT_DISTANCE = 3;
// Штраф позиции молодого великана за каждый отряд игрока-противника в
// досягаемости, не меньший по размеру двух третей великана
export const W39_GIANTS_YOUNG_THREAT_PENALTY = 12;
export const W39_GIANTS_YOUNG_THREAT_SIZE_SHARE = 2 / 3;

// Рост на своей казарме: каждое снабжение великану там дает +2 вместо +1 и без
// предела «раз за ход», то есть казарма удваивает весь рост от ферм. Общий
// бонус «стоять на казарме» (6) — для обычного отряда, у которого снабжение одно;
// великан уходил с казармы уже на размере ~11 (пилот волны 39). Надбавка за
// каждую свою ферму, пока великан ниже порога удержания (доля цели роста)
export const W39_GIANTS_BARRACKS_STAY_PER_FARM = 3;
export const W39_GIANTS_BARRACKS_HOLD_SHARE = 0.6;
export const W39_GIANTS_BARRACKS_HOLD_SHARE_CAMP = 0.8;

// Ближний бой — только от численного превосходства: размер великана не меньше
// суммы смежных врагов × этот множитель (giants.md: «в 1.5-2 раза больше цели»).
// Иначе позиция штрафуется, кроме обороны квартала своего города
export const W39_GIANTS_ENGAGE_RATIO = 1.5;
// Великан — до 5 очков противнику и многораундовые вложения снабжения: урон,
// полученный им, весит больше (как у эльфов, у которых рост медленный)
export const W39_GIANTS_RECEIVED_DAMAGE_WEIGHT = 1.4;

// Цель числа ферм: каждая лишняя ферма великанам — немедленный рост без
// предела «1 раз за ход» (giants.md). База на первый раунд и +1 за каждые два
// раунда, не больше максимума
export const W39_GIANTS_FARMS_BASE = 3;
export const W39_GIANTS_FARMS_PER_TWO_ROUNDS = 1;
export const W39_GIANTS_FARMS_MAX = 8;

// --- Суккубы

// Нижний порог шанса соблазнения (d20 + разница численности ≥ порога): разница
// −1 — 30%, 0 — 35% (succubes.md: пробуем при шансе от 30%). Ниже — только
// когда монеты не нужны ни на что другое (минимум MIN_CHANCE_SPARE)
export const W39_SUCCUBES_MIN_CHANCE = 0.3;
export const W39_SUCCUBES_MIN_CHANCE_SPARE = 0.15;
// «Свободные» монеты для слабых попыток: сверх этого запаса
export const W39_SUCCUBES_SPARE_COINS = 12;

// Ценность цели в монетах: цена найма ее типа + 2 монеты за единицу размера
// (докупка размера) — столько стоил бы такой же отряд с нуля
export const W39_SUCCUBES_SIZE_COINS = 2;
// Бескровный захват города: единственный защитник на кварталах — соблазнение
// снимает город с обороны, и он переходит суккубам в конце раунда (succubes.md).
// Ценность — в монетах, сверх ценности самого отряда
export const W39_SUCCUBES_CITY_CAPTURE_COINS = 14;

// Соблазнитель — отряд суккубов не меньше этого размера (не катапульта)
export const W39_SUCCUBES_SEDUCER_MIN_SIZE = 4;
// Вес ожидаемой ценности соблазнения (монеты) в оценке позиции перемещения:
// позиция в 2–3 клетках от цели с прямой видимостью и без смежного врага.
// Монета кражи лепреконов в той же оценке весит 4 (LEPRECHAUN_STEAL_VALUE_FACTOR);
// соблазнение — вероятностное и требует, чтобы цель не ушла, поэтому меньше
export const W39_SUCCUBES_POSITION_WEIGHT = 3;
// Потолок надбавки позиции — не перебивает захват (100) и оборону городов (30)
export const W39_SUCCUBES_POSITION_MAX = 30;
// Дальность «цель скоро будет в радиусе»: радиус соблазнения + скорость пехоты
// — для резерва монет в стройке и найме
export const W39_SUCCUBES_RESERVE_REACH = 5;
// Резерв монет на попытки этого хода: не больше стольких попыток
export const W39_SUCCUBES_RESERVE_MAX_ATTEMPTS = 2;

// --- Гоблины

// Ценность повреждения строения противника (монеты): поврежденное строение не
// работает до ремонта (действие стройки), второе повреждение его разрушает
// (game_rules.md). Доход (гильдия, храм) и армия (фермы, казармы) — дороже
export const W39_GOBLINS_BUILDING_DAMAGE_VALUES = {
  guild: 7,
  temple: 7,
  farm: 5,
  barracks: 5,
  warehouse: 5,
  military_camp: 5,
  arsenal: 4,
  palace: 4,
  quarter: 3,
  centralQuarter: 6,
  fortress: 3,
  default: 3,
};
// Разрушение (второе повреждение) — строение пропадает насовсем: ценность ×
export const W39_GOBLINS_DESTROY_MULTIPLIER = 1.6;

// Ловушка: цена попытки 1 монета (успех 3+ на d6), снятие возвращает монету.
// Ставим на подступах к своим городам, когда враг рядом: ячейка срабатывания —
// сама ловушка и смежные ей. Ценность — в монетах
// Радиус «враг у города»: отряд игрока-противника не дальше стольких клеток от
// кварталов своего города
export const W39_GOBLINS_TRAP_THREAT_DISTANCE = 6;
// Минимальная ценность ловушки, чтобы платить за попытку
export const W39_GOBLINS_TRAP_MIN_VALUE = 2;
// Ценность ловушки на подступе: базовая + за каждый размер врага в радиусе
// угрозы (до потолка), × доля новых клеток срабатывания (не перекрытых своими
// ловушками)
export const W39_GOBLINS_TRAP_BASE_VALUE = 2;
export const W39_GOBLINS_TRAP_VALUE_PER_ENEMY_SIZE = 0.25;
export const W39_GOBLINS_TRAP_MAX_VALUE = 6;
// Монеты, которые попытка ловушки оставляет на руках (найм и стройка важнее)
export const W39_GOBLINS_TRAP_MIN_COINS_LEFT = 2;

// Вес ожидаемой ценности повреждения строения в оценке позиции перемещения и
// потолок надбавки (не перебивает захват и оборону)
export const W39_GOBLINS_POSITION_WEIGHT = 2.5;
export const W39_GOBLINS_POSITION_MAX = 24;

// Гильдии и храмы — экономика оборонной фракции (goblins.md): множитель их
// ценности в стройке
export const W39_GOBLINS_INCOME_SPECIAL_MULTIPLIER = 1.4;
